import { apiGet, apiGetPaginated, apiPatch, apiPost, PaginatedResult } from '../client'
import { StudentImmigrationTopic } from './appointments'

/**
 * Where a consultation request stands. INITIAL_CONSULTATION_COMPLETED to
 * NOT_RETAINED belong to the retired referral funnel and only appear on older
 * records; nothing moves a request into them any more.
 */
export type ReferralStatus =
  | 'REQUESTED'
  | 'ACCEPTED'
  | 'DECLINED'
  | 'SCHEDULED'
  | 'COMPLETED'
  | 'CLOSED'
  | 'INITIAL_CONSULTATION_COMPLETED'
  | 'FOLLOW_UP_COMPLETED'
  | 'RETAINED'
  | 'NOT_RETAINED'

/** The attorney's tabs: new requests, accepted and waiting to be booked, booked, finished. */
export type ConsultationStage = 'history' | 'new' | 'pending' | 'scheduled'

export type ConsultationMode = 'IN_PERSON' | 'VIRTUAL'

export interface ConnectionPartySummary {
  /** booking details: the requester gets these once the attorney accepts */
  availabilityNote?: string | null,
  avatarUrl?: string | null,
  companyName?: string
  consultationModes?: ConsultationMode[]
  email?: string | null
  firmWebsite?: string | null
  firstName: string
  id: string
  lastName: string
  lawFirmName?: string
  phoneNumber?: string | null
  schedulingUrl?: string | null
  userId: string
}

export interface ConnectionRequest {
  [key: string]: unknown
  acceptedAt?: string | null
  closedAt?: string | null
  closedReason?: string | null
  completedAt?: string | null
  createdAt: string
  declineReason?: string | null
  description: string
  employer?: ConnectionPartySummary | null
  employerId?: string | null
  focusArea: StudentImmigrationTopic
  focusAreaDetails?: string | null
  id: string
  lawyer: ConnectionPartySummary
  lawyerId: string
  /** older records from the retired referral flow */
  notRetainedReason?: string | null
  respondedAt?: string | null
  scheduledAt?: string | null
  scheduledFor?: string | null
  status: ReferralStatus
  student?: ConnectionPartySummary | null
  studentId?: string | null
  updatedAt: string
}

export interface CreateConnectionRequestPayload {
  description: string
  focusArea: StudentImmigrationTopic
  focusAreaDetails?: string
  lawyerProfileId: string
}

export interface DeclineConnectionRequestPayload {
  reason?: string
}

export interface RespondToConnectionRequestPayload {
  message: string
}

/** Where an accepted consultation can go next, set by the attorney. */
export type ConsultationUpdate = 'CLOSED' | 'COMPLETED' | 'SCHEDULED'

export interface UpdateConsultationPayload {
  reason?: string
  scheduledFor?: string
  status: ConsultationUpdate
}

/** Whether students and employers can see attorneys yet, and whether attorneys can sign the agreement. */
export interface AttorneyNetworkStatus {
  agreementVersion: string
  open: boolean
  signingEnabled: boolean
}

export const getAttorneyNetworkStatus = () => apiGet<AttorneyNetworkStatus>('/attorney-network/status')

export const createConnectionRequest = (payload: CreateConnectionRequestPayload) =>
  apiPost<ConnectionRequest>('/attorney-connections', payload)

export const listMyConnectionRequests = (params?: { limit?: number; page?: number; status?: ReferralStatus }): Promise<PaginatedResult<ConnectionRequest[]>> =>
  apiGetPaginated<ConnectionRequest[]>('/attorney-connections/mine', { params })

export const listLawyerConnectionRequests = (params?: { limit?: number; page?: number; stage?: ConsultationStage; status?: ReferralStatus }): Promise<PaginatedResult<ConnectionRequest[]>> =>
  apiGetPaginated<ConnectionRequest[]>('/attorney-connections/lawyer', { params })

export const getConnectionRequest = (id: string) => apiGet<ConnectionRequest>(`/attorney-connections/${id}`)

export const acceptConnectionRequest = (id: string) => apiPatch<ConnectionRequest>(`/attorney-connections/${id}/accept`, {})

export const declineConnectionRequest = (id: string, payload: DeclineConnectionRequestPayload) =>
  apiPatch<ConnectionRequest>(`/attorney-connections/${id}/decline`, payload)

export const respondToConnectionRequest = (id: string, payload: RespondToConnectionRequestPayload) =>
  apiPost<ConnectionRequest & { conversationId?: string }>(`/attorney-connections/${id}/respond`, payload)

export const updateConsultation = (id: string, payload: UpdateConsultationPayload) =>
  apiPatch<ConnectionRequest>(`/attorney-connections/${id}/status`, payload)
